import {
	ANALYTICS_EVENT_ADD_PAYMENT_INFO,
	ANALYTICS_EVENT_ADD_SHIPPING_INFO,
	ANALYTICS_EVENT_ADD_TO_CART,
	ANALYTICS_EVENT_BEGIN_CHECKOUT,
	ANALYTICS_EVENT_BUYLIST_SUBMIT,
	ANALYTICS_EVENT_PURCHASE,
	ANALYTICS_EVENT_REMOVE_FROM_CART,
	ANALYTICS_EVENT_VIEW_CART,
	ANALYTICS_EVENT_VIEW_ITEM,
	ANALYTICS_EVENT_VIEW_ITEM_LIST,
	trackAnalyticsEvent,
} from '@/lib/tracking';

import { getCurrencyCode } from './currency';


// These helpers build the canonical (GA4-shaped) ecommerce payload from
// product / cart / order domain objects, then hand it to the unified tracking
// dispatcher. Provider-specific routing and remapping live in
// src/lib/tracking/analytics-events.ts.


export function viewItemListEvent(products, config = {}) {

	const itemsArray = [];

	try {

		for(let i = 0; i < products.length; i++) {
			itemsArray.push(products[i].getEventItemData({
				index: i,
				isBuylist: config.isBuylist || false,
				item_list_id: config.item_list_id || '',
				item_list_name: config.item_list_name || '',
			}));
		}

		const eventData = {
			item_list_id: config.item_list_id || 'product_gallery',
			item_list_name: 'Product gallery',
			items: itemsArray,
		};
		trackAnalyticsEvent(ANALYTICS_EVENT_VIEW_ITEM_LIST, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function viewItemEvent(products, config = {}) {

	const itemsArray = [];

	try {

		let subtotal = 0;
		for(let i = 0; i < products.length; i++) {
			itemsArray.push(products[i].getEventItemData({
				index: i,
				isBuylist: config.isBuylist || false,
				item_list_id: config.item_list_id || '',
				item_list_name: config.item_list_name || '',
			}));
			subtotal += parseFloat(products[i].getMaxPrice()) || 0;
		}

		const eventData = {
			currency: getCurrencyCode(),
			value: subtotal,
			items: itemsArray,
		};
		trackAnalyticsEvent(ANALYTICS_EVENT_VIEW_ITEM, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function addToCartEvent(inventory, product, config = {}) {

	const itemsArray = [];

	try {

		itemsArray.push(product.getEventItemData({
			isBuylist: config.isBuylist || false,
			price: config.price || inventory.sellPrice,
			quantity: config.quantity || 1,
		}));

		const eventData = {
			currency: getCurrencyCode(),
			value: parseFloat(config.price || inventory.sellPrice) || 0,
			items: itemsArray,
		};
		trackAnalyticsEvent(ANALYTICS_EVENT_ADD_TO_CART, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function removeFromCartEvent(inventory, product, config = {}) {

	const itemsArray = [];

	try {

		itemsArray.push(product.getEventItemData({
			isBuylist: config.isBuylist || false,
			price: config.price || inventory.sellPrice,
			quantity: config.quantity || 1,
		}));

		const eventData = {
			currency: getCurrencyCode(),
			value: parseFloat(config.price || inventory.sellPrice) || 0,
			items: itemsArray,
		};
		trackAnalyticsEvent(ANALYTICS_EVENT_REMOVE_FROM_CART, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function viewCartEvent(cart, config = {}) {

	const itemsArray = [];

	try {

		for(let i = 0; i < cart.items.length; i++) {
			itemsArray.push(cart.items[i].product.getEventItemData({
				index: i,
				isBuylist: config.isBuylist || false,
				price: config.price || cart.items[i].price || 0,
				quantity: config.quantity || cart.items[i].quantity || 1,
			}));
		}

		const eventData = {
			currency: getCurrencyCode(),
			value: cart.subTotal,
			items: itemsArray,
		};
		trackAnalyticsEvent(ANALYTICS_EVENT_VIEW_CART, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function beginCheckoutEvent(cart, config = {}) {

	const itemsArray = [];

	try {

		for(let i = 0; i < cart.items.length; i++) {
			itemsArray.push(cart.items[i].product.getEventItemData({
				index: i,
				isBuylist: config.isBuylist || false,
				price: config.price || cart.items[i].price || 0,
				quantity: config.quantity || cart.items[i].quantity || 1,
			}));
		}

		const eventData = {
			currency: getCurrencyCode(),
			value: cart.subTotal,
			items: itemsArray,
		};
		if(cart.coupon) {
			eventData['coupon'] = cart.coupon.code.toUpperCase();
		}
		trackAnalyticsEvent(ANALYTICS_EVENT_BEGIN_CHECKOUT, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function addShippingInfoEvent(cart, shippingMethod, config = {}) {

	const itemsArray = [];

	try {

		for(let i = 0; i < cart.items.length; i++) {
			itemsArray.push(cart.items[i].product.getEventItemData({
				index: i,
				isBuylist: config.isBuylist || false,
				price: config.price || cart.items[i].price || 0,
				quantity: config.quantity || cart.items[i].quantity || 1,
			}));
		}

		const eventData = {
			currency: getCurrencyCode(),
			value: cart.subTotal,
			items: itemsArray,
			shipping_tier: shippingMethod.name,
		};
		if(cart.coupon) {
			eventData['coupon'] = cart.coupon.code.toUpperCase();
		}
		trackAnalyticsEvent(ANALYTICS_EVENT_ADD_SHIPPING_INFO, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function addPaymentInfoEvent(cart, paymentMethod, config = {}) {

	const itemsArray = [];

	try {

		for(let i = 0; i < cart.items.length; i++) {
			itemsArray.push(cart.items[i].product.getEventItemData({
				index: i,
				isBuylist: config.isBuylist || false,
				price: config.price || cart.items[i].price || 0,
				quantity: config.quantity || cart.items[i].quantity || 1,
			}));
		}

		const eventData = {
			currency: getCurrencyCode(),
			value: cart.subTotal,
			items: itemsArray,
			payment_type: paymentMethod.name,
		};
		if(cart.coupon) {
			eventData['coupon'] = cart.coupon.code.toUpperCase();
		}
		trackAnalyticsEvent(ANALYTICS_EVENT_ADD_PAYMENT_INFO, eventData);

	} catch(err) {
		console.error(err);
	}
}

export function purchaseEvent(order, config = {}) {

	const itemsArray = [];

	try {

		// TODO: modify so we can accomodate buylist carts, if needed
		if(!order || !order.cart) {
			return null;
		}

		for(let i = 0; i < order.cart.items.length; i++) {
			itemsArray.push(order.cart.items[i].product.getEventItemData({
				index: i,
				isBuylist: config.isBuylist || false,
				price: config.price || order.cart.items[i].price || 0,
				quantity: config.quantity || order.cart.items[i].quantity || 1,
			}));
		}

		const eventData = {
			transaction_id: order.displayNumber,
			currency: getCurrencyCode(),
			value: order.totalCart,
			tax: order.totalTax,
			shipping: order.totalShipping,
			items: itemsArray,
		};
		if(order.coupon) {
			eventData['coupon'] = order.coupon.code.toUpperCase();
		}
		trackAnalyticsEvent(ANALYTICS_EVENT_PURCHASE, eventData);

	} catch(err) {
		console.error(err);
	}
}

// A buylist is the store buying inventory FROM the customer, so this fires the
// distinct `buylist_submit` event (Google only) rather than `purchase` — a
// buylist is procurement, not revenue, and must stay out of ecommerce revenue
// reporting. Reads from order.buylistCart (not order.cart).
export function buylistSubmitEvent(order, config = {}) {

	const itemsArray = [];

	try {

		if(!order || !order.buylistCart) {
			return null;
		}

		const items = order.buylistCart.items || [];
		for(let i = 0; i < items.length; i++) {
			itemsArray.push(items[i].product.getEventItemData({
				index: i,
				isBuylist: true,
				price: config.price || items[i].price || 0,
				quantity: config.quantity || items[i].quantity || 1,
			}));
		}

		const eventData = {
			transaction_id: order.displayNumber,
			currency: getCurrencyCode(),
			value: order.totalCart,
			items: itemsArray,
		};
		trackAnalyticsEvent(ANALYTICS_EVENT_BUYLIST_SUBMIT, eventData);

	} catch(err) {
		console.error(err);
	}
}
